import React, {useCallback, useState} from "react";
import {Box, Step, StepContent, StepLabel, Stepper} from "@mui/material";
import cx from "classnames";
import {IntegrationFormStepsStyle} from "../../../constants/AppConstants";
import {IntegrationAuthFields} from "./IntegrationAuthFields";
import {ApiEndPointEditor} from "../../apiEndpointEditor/ApiEndPointEditor";
import {useIntegrationsContext} from "../../../api/integrations/IntegrationsContext";
import {useParams} from "react-router";
import {showError} from "../../../utils/NotificationUtils";
import {toast} from "react-toastify";
import {AuthTest} from "../../connections/AuthTest";
import {IntegrationAuthRedirectUrl} from "./IntegrationAuthRedirectURL";

interface Props {
    readonly [key: string]: any;
}

export function IntegrationAuthSteps({...props}: Props) {
    const classes = IntegrationFormStepsStyle();
    const [activeSteps, setActiveSteps] = useState<number[]>([0]);
    const [saving, setSaving] = useState(false);
    const {IntegrationsApi} = useIntegrationsContext();
    const params: any = useParams();

    const onSetActive = (num: number) => {
        setActiveSteps((prev) => {
            if (prev.includes(num)) {
                const idx = prev.indexOf(num);
                return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
            } else {
                return [...prev, num];
            }
        });
    };
    const handleTestRequestSubmit = useCallback(
        (values) => {
            setSaving(true);
            IntegrationsApi.configureTestRequest({
                data: {
                    connectionLabel: props.data.integration.name,
                    request: {
                        method: values.method,
                        params: values.params,
                        headers: values.headers,
                        requestBody: values.requestBody,
                        source: values.source,
                        switchedToSource: values.switchedToSource,
                        url: values.url,
                    },
                },
                params: {
                    id: params.authId,
                },
            })
                .then(() => {
                    toast.success("Saved");
                    setSaving(false)
                })
                .catch((err) => {
                    showError(err);
                    setSaving(false)
                });
        },
        [params, IntegrationsApi]
    );

    const handleConfigureRequest = useCallback(
        (values, endpoint) => {
            setSaving(true);
            //@ts-ignore
            IntegrationsApi[endpoint]({
                data: {
                    method: values.method,
                    params: values.params,
                    headers: values.headers,
                    requestBody: values.requestBody,
                    source: values.source,
                    switchedToSource: values.switchedToSource,
                    url: values.url,
                },
                params: {
                    id: params.authId,
                },
            })
                .then(() => {
                    toast.success("Saved");
                    setSaving(false);
                })
                //@ts-ignore
                .catch((err) => {
                    showError(err);
                    setSaving(false);
                });
        },
        [params, IntegrationsApi]
    );

    const isBasicAuth = props.data?.authType === "BASIC_AUTH";
    const isOAuth2 = props.data?.authType === "OAUTH2";
    const isSessionAuth = props.data?.authType === "SESSION_AUTH";

    return (
        <Box className={classes.root}>
            <Stepper orientation="vertical">
                {!isBasicAuth && (
                    <Step
                        active={activeSteps.includes(0)}
                        className={cx(classes.stepRoot, {
                            active: activeSteps.includes(0),
                        })}
                    >
                        <StepLabel onClick={() => onSetActive(0)}>
                            <div className={classes.stepOrder}>
                                Step <span>{1}</span>
                            </div>
                            <span className={classes.labelTitle}>Configure your Fields</span>
                        </StepLabel>
                        <StepContent>
                            <div className={classes.content}>
                                <IntegrationAuthFields
                                    fields={props.fields}
                                    onChange={(value: any) => {
                                        props.onChange("fields", value);
                                    }}
                                />
                            </div>
                        </StepContent>
                    </Step>
                )}
                {isOAuth2 && (
                    <Step
                        active={activeSteps.includes(1)}
                        className={cx(classes.stepRoot, {
                            active: activeSteps.includes(1),
                        })}
                    >
                        <StepLabel onClick={() => onSetActive(1)}>
                            <div className={classes.stepOrder}>Step{" "}<span>{2}</span></div>
                            <span className={classes.labelTitle}>
                Copy your OAuth Redirect URL
              </span>
                        </StepLabel>
                        <StepContent>
                            <div className={classes.content}>
                                <IntegrationAuthRedirectUrl/>
                            </div>
                        </StepContent>
                    </Step>
                )}
                {isOAuth2 && (
                    <Step
                        active={activeSteps.includes(2)}
                        className={cx(classes.stepRoot, classes.disabledStep, {
                            active: activeSteps.includes(2),
                        })}
                    >
                        <StepLabel onClick={() => onSetActive(2)}>
                            <div className={classes.stepOrder}>Step{" "}<span>{3}</span></div>
                            <span className={classes.labelTitle}>
              Enter your Application Credentials
            </span>
                        </StepLabel>
                        <StepContent>
                            <div className={classes.content}>Application Credentials</div>
                        </StepContent>
                    </Step>
                )}

                {isOAuth2 && (
                    <Step
                        active={activeSteps.includes(3)}
                        className={cx(classes.stepRoot, {
                            active: activeSteps.includes(3),
                        })}
                    >
                        <StepLabel onClick={() => onSetActive(3)}>
                            <div className={classes.stepOrder}>Step{" "}<span>{4}</span></div>
                            <span className={classes.labelTitle}>Authorization Request</span>
                        </StepLabel>
                        <StepContent>
                            <div className={classes.content}>
                                <ApiEndPointEditor
                                    apiEndpoint={props.data?.authorizationRequest}
                                    handleSubmit={(data) => handleConfigureRequest(data, "configureAuthorizationRequest")}
                                    onChange={(data) =>
                                        props.onChange("authorizationRequest", data)
                                    }
                                />
                            </div>
                        </StepContent>
                    </Step>
                )}
                {isOAuth2 && (
                    <Step
                        active={activeSteps.includes(4)}
                        className={cx(classes.stepRoot, {
                            active: activeSteps.includes(4),
                        })}
                    >
                        <StepLabel onClick={() => onSetActive(4)}>
                            <div className={classes.stepOrder}>Step{" "}<span>{5}</span></div>
                            <span className={classes.labelTitle}>Access Token Request</span>
                        </StepLabel>
                        <StepContent>
                            <div className={classes.content}>
                                <ApiEndPointEditor
                                    apiEndpoint={props.data?.accessTokenRequest}
                                    handleSubmit={(data) => handleConfigureRequest(data, "configureAccessTokenRequest")}
                                    onChange={(data) =>
                                        props.onChange("accessTokenRequest", data)
                                    }
                                />
                            </div>
                        </StepContent>
                    </Step>
                )}
                {(isSessionAuth || isOAuth2) && (
                    <Step
                        active={activeSteps.includes(5)}
                        className={cx(classes.stepRoot, {
                            active: activeSteps.includes(5),
                        })}
                    >
                        <StepLabel onClick={() => onSetActive(5)}>
                            <div className={classes.stepOrder}>Step{" "}<span>{isOAuth2 ? 6 : 2}</span></div>
                            <span className={classes.labelTitle}>
                    {isOAuth2 ? "Refresh token Request" : "Configure a token exchange request"}
              </span>
                        </StepLabel>
                        <StepContent>
                            <div className={classes.content}>
                                <ApiEndPointEditor
                                    apiEndpoint={props.data?.tokenExchangeRequest}
                                    handleSubmit={(data) => handleConfigureRequest(data, "configureTokenExchangeRequest")}
                                    onChange={(data) =>
                                        props.onChange("tokenExchangeRequest", data)
                                    }
                                />
                            </div>
                        </StepContent>
                    </Step>
                )}
                <Step
                    active={activeSteps.includes(6)}
                    className={cx(classes.stepRoot, {
                        active: activeSteps.includes(6),
                    })}
                >
                    <StepLabel onClick={() => onSetActive(6)}>
                        <div className={classes.stepOrder}>
                            Step <span>{isOAuth2 ? 7 : isBasicAuth ? 1 : 3}</span>
                        </div>
                        <span className={classes.labelTitle}>
              Configure a Test Request & Connection Label
            </span>
                    </StepLabel>
                    <StepContent>
                        <div className={classes.content}>
                            <ApiEndPointEditor
                                authEditor
                                apiEndpoint={props.data?.testRequest}
                                handleSubmit={(data) => handleTestRequestSubmit(data)}
                                onChange={(data) => props.onChange("testRequest", data)}
                            />
                        </div>
                    </StepContent>
                </Step>
                <Step
                    active={activeSteps.includes(7)}
                    className={cx(classes.stepRoot, {
                        active: activeSteps.includes(7),
                    })}
                >
                    <StepLabel onClick={() => onSetActive(7)}>
                        <div className={classes.stepOrder}>
                            Step <span>{isOAuth2 ? 8 : isBasicAuth ? 2 : 4}</span>
                        </div>
                        <span className={classes.labelTitle}>Test</span>
                    </StepLabel>
                    <StepContent>
                        <div className={classes.content}>
                            <AuthTest isOAuth2={isOAuth2} />
                        </div>
                    </StepContent>
                </Step>
            </Stepper>
        </Box>
    );
}